import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent } from '@/components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { useAuth } from '../context/AuthContext';
import { userMenuApi, menuCategoryApi, menuSubCategoryApi } from '../config/api';
import { useIsMobile } from '../hooks/use-is-mobile';
import { cn } from '@/lib/utils';

const Login = () => {
  const [nik, setNik] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [userRoles, setUserRoles] = useState([]);
  const [loginResult, setLoginResult] = useState(null);
  const { login, logout, setSelectedRole } = useAuth();
  const navigate = useNavigate();
  const [isRoleModalOpen, setIsRoleModalOpen] = useState(false);
  const isMobile = useIsMobile();

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!nik || !password) {
      toast.warning('NIK dan password harus diisi', { description: 'Validasi gagal' });
      return;
    }

    setLoading(true);

    try {
      const result = await login(nik, password);

      if (result.success) {
        // Super admin bypass — full access, no menu check needed
        const userData = JSON.parse(localStorage.getItem('userData') || '{}');
        if (userData.admin_status === 'admin') {
          toast.success(`Selamat datang, ${result.data.name}`, { description: 'Login berhasil — Super Admin' });
          setLoading(false);
          navigate('/admin');
          return;
        }

        try {
          const integratedToken = localStorage.getItem('integratedToken');
          if (!integratedToken) {
            toast.error('Token tidak ditemukan. Silakan login ulang.', { description: 'Error' });
            setLoading(false);
            logout();
            return;
          }

          const [userMenusResponse, categoriesResponse, subCategoriesResponse] = await Promise.all([
            userMenuApi.get(`/list/${nik}`),
            menuCategoryApi.get('/list'),
            menuSubCategoryApi.get('/list'),
          ]);

          const userMenus = userMenusResponse.data.status === 200 ? userMenusResponse.data.data || [] : [];
          const categories = categoriesResponse.data.status === 200 ? categoriesResponse.data.data || [] : [];
          const subCategories = subCategoriesResponse.data.status === 200 ? subCategoriesResponse.data.data || [] : [];

          const hasBackendMenu = userMenus.some((userMenu) => {
            const subCategory = subCategories.find((sub) => sub.MENUSUBCATEGORY_ID === userMenu.MENUSUBCATEGORY_ID);
            if (!subCategory) return false;
            const category = categories.find((cat) => cat.MENUCATEGORY_ID === subCategory.MENUCATEGORY_ID);
            return category && category.MENUCATEGORY_TYPE === 'be';
          });

          if (!hasBackendMenu) {
            toast.error('User Tidak Terdaftar di Back End', { description: 'Akses ditolak' });
            logout();
            setLoading(false);
            return;
          }

          const role = userData.role || '';

          let roles = [];
          if (role) {
            if (Array.isArray(role)) {
              roles = role;
            } else if (typeof role === 'string') {
              if (role.includes(';')) {
                roles = role.split(';').map((r) => r.trim()).filter((r) => r);
              } else if (role.includes(',')) {
                roles = role.split(',').map((r) => r.trim()).filter((r) => r);
              } else if (role.trim()) {
                roles = [role.trim()];
              }
            }
          }

          if (roles.length > 1) {
            setUserRoles(roles);
            setLoginResult(result);
            setIsRoleModalOpen(true);
          } else {
            if (roles.length === 1) {
              localStorage.setItem('selectedRole', roles[0]);
            }
            toast.success(`Selamat datang, ${result.data.name}`, { description: 'Login berhasil' });
            navigate('/admin');
          }
        } catch (menuCheckError) {
          console.error('Error checking backend menu:', menuCheckError);
          const status = menuCheckError.response?.status;
          const apiMessage = menuCheckError.response?.data?.message;
          const networkDown = !menuCheckError.response
            || status === 502
            || status === 503
            || status === 504;
          toast.error(
            networkDown
              ? 'Layanan API PRIME sedang tidak tersedia. Coba lagi nanti atau hubungi administrator.'
              : (apiMessage || 'Gagal memverifikasi akses menu. Silakan hubungi administrator.'),
            { description: networkDown ? `Backend error${status ? ` (${status})` : ''}` : 'Error' },
          );
          logout();
          setLoading(false);
          return;
        }
      } else {
        toast.error(result.message || 'NIK atau password salah', { description: 'Login gagal' });
      }
    } catch (error) {
      toast.error('Terjadi kesalahan saat login', { description: 'Error' });
    } finally {
      setLoading(false);
    }
  };

  const formatRoleLabel = (role) => {
    const roleMap = {
      ieem: 'IEEM (Injourney Employee Exclusive Membership)',
      bp: 'Bundling Product',
    };
    return roleMap[role] || role;
  };

  const handleRoleSelect = (role) => {
    localStorage.setItem('selectedRole', role);
    if (setSelectedRole) {
      setSelectedRole(role);
    }
    setIsRoleModalOpen(false);
    if (loginResult) {
      toast.success(`Selamat datang, ${loginResult.data.name}`, { description: 'Login berhasil' });
    }
    navigate('/admin');
  };

  return (
    <div className="flex min-h-screen bg-slate-50">
      {!isMobile && (
        <div
          className="relative hidden flex-1 items-center justify-center overflow-hidden lg:flex"
          style={{ background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' }}
        >
          <div
            className="pointer-events-none absolute inset-0 opacity-10"
            style={{
              backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='1'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
            }}
          />
          <div className="relative z-[1] flex flex-col items-center gap-8 px-12 text-center text-white">
            <div>
              <h1 className="mb-4 text-4xl font-bold tracking-tight">Injourney Inventory</h1>
              <p className="text-xl font-medium text-white/90">Admin Dashboard</p>
            </div>
            <p className="mt-8 max-w-md text-lg text-white/80">Kelola inventaris dengan mudah dan efisien</p>
          </div>
        </div>
      )}

      <div className="flex flex-1 items-center justify-center px-4 py-8 md:px-8 lg:px-12 lg:py-12">
        <div className="w-full max-w-[450px]">
          {isMobile && (
            <div className="mb-8 text-center">
              <h1 className="mb-2 text-2xl font-bold text-brand-600">Injourney Inventory</h1>
              <p className="text-sm text-slate-600">Admin Dashboard</p>
            </div>
          )}

          <div className="rounded-2xl bg-white p-6 shadow-xl md:p-8 md:shadow-[0_20px_25px_-5px_rgba(0,0,0,0.1),0_10px_10px_-5px_rgba(0,0,0,0.04)]">
            <div className="flex flex-col gap-6">
              {!isMobile && (
                <div>
                  <h2 className="mb-2 text-xl font-bold text-slate-800">Sign In</h2>
                  <p className="text-sm text-slate-600">Enter your NIK and password to sign in</p>
                </div>
              )}

              <div className="mb-2 rounded-xl border border-blue-200 bg-blue-50 p-4">
                <p className="mb-1 text-xs font-semibold uppercase tracking-wide text-blue-800">Login menggunakan akun Injourney Group</p>
                <p className="text-xs text-blue-700">Masukkan NIK dan password Anda</p>
              </div>

              <form onSubmit={handleSubmit}>
                <div className="flex flex-col gap-5">
                  <div className="space-y-2">
                    <Label htmlFor="nik" className="text-sm font-semibold text-slate-700">
                      NIK
                    </Label>
                    <Input
                      id="nik"
                      required
                      type="text"
                      placeholder="Masukkan NIK"
                      value={nik}
                      onChange={(e) => setNik(e.target.value)}
                      className="h-12 rounded-xl border-slate-200 bg-slate-50 focus-visible:ring-brand-500"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="password" className="text-sm font-semibold text-slate-700">
                      Password
                    </Label>
                    <Input
                      id="password"
                      required
                      type="password"
                      placeholder="Masukkan password"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      className="h-12 rounded-xl border-slate-200 bg-slate-50 focus-visible:ring-brand-500"
                    />
                  </div>
                  <Button type="submit" disabled={loading} className="mt-2 h-12 w-full rounded-xl font-semibold">
                    {loading ? 'Signing in...' : 'Sign In'}
                  </Button>
                </div>
              </form>
            </div>
          </div>

          <p className="mt-6 text-center text-sm text-slate-500">© {new Date().getFullYear()} Injourney. All rights reserved.</p>
        </div>
      </div>

      <Dialog open={isRoleModalOpen} onOpenChange={() => {}}>
        <DialogContent
          hideClose
          className="max-w-[500px] rounded-2xl"
          onPointerDownOutside={(e) => e.preventDefault()}
          onEscapeKeyDown={(e) => e.preventDefault()}
        >
          <DialogHeader>
            <DialogTitle className="text-slate-800">Pilih Role</DialogTitle>
            <DialogDescription>
              Anda memiliki beberapa role. Silakan pilih role yang ingin digunakan untuk sesi ini.
            </DialogDescription>
          </DialogHeader>
          <div className="flex flex-col gap-3">
            {userRoles.map((role, index) => (
              <Card
                key={index}
                className={cn(
                  'cursor-pointer border-2 border-slate-200 transition-all hover:-translate-y-0.5 hover:shadow-md'
                )}
                onClick={() => handleRoleSelect(role)}
              >
                <CardContent className="p-4">
                  <div className="flex flex-col gap-2 text-left">
                    <p className="text-base font-semibold text-slate-800">{formatRoleLabel(role)}</p>
                    <p className="text-sm text-slate-600">Masuk sebagai admin {formatRoleLabel(role)}</p>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
          <DialogFooter>
            <p className="text-xs text-slate-500">Anda dapat mengganti role dengan logout dan login ulang</p>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Login;
